import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {RootState} from '@/store';

/**
 * Ephemeral per-session state the poster editor produces at high frequency: playback ticks and
 * in-progress canvas gestures. Kept apart from the poster mirror so a tick never changes the
 * document's identity, and so only the playhead, slider and control overlays need to listen.
 */
interface PosterSessionState {
  /** Playhead time per page, written by each page's clock on every tick. */
  playbackTime: Record<string, number>;
  /** Bumped on every move/scale/rotate tick on a page; overlays that follow the live Fabric object re-measure on change. */
  itemTransformVersion: Record<string, number>;
}

interface PagePlaybackTimePayload {
  pageHashId: string;
  currentTime: number;
}

const initialState: PosterSessionState = {
  playbackTime: {},
  itemTransformVersion: {},
};

const posterSessionSlice = createSlice({
  name: 'posterSession',
  initialState,
  reducers: {
    updatePagePlaybackTime: (state, action: PayloadAction<PagePlaybackTimePayload>): void => {
      state.playbackTime[action.payload.pageHashId] = action.payload.currentTime;
    },
    notifyItemTransformChanged: (state, action: PayloadAction<string>): void => {
      state.itemTransformVersion[action.payload] = (state.itemTransformVersion[action.payload] ?? 0) + 1;
    },
    resetPosterSession: (): PosterSessionState => initialState,
  },
});

export const {updatePagePlaybackTime, notifyItemTransformChanged, resetPosterSession} = posterSessionSlice.actions;
export const posterSessionReducer = posterSessionSlice.reducer;

export const getPagePlaybackTime = (state: RootState, pageId: string): number => {
  return state.posterSession.playbackTime[pageId] ?? 0;
};

export const getPageItemTransformVersion = (state: RootState, pageId: string): number => {
  return state.posterSession.itemTransformVersion[pageId] ?? 0;
};
